import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import StepProgressIndicator from '../../components/ui/StepProgressIndicator';
import WorkflowBreadcrumb from '../../components/ui/WorkflowBreadcrumb';
import NavigationActions from '../../components/ui/NavigationActions';
import ParticipantList from './components/ParticipantList';
import QuickStartGuide from './components/QuickStartGuide';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import InviteParticipantModal from './components/InviteParticipantModal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createMeeting } from '../../services/meetingService';



const LocationInput = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isCalculating, setIsCalculating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errors, setErrors] = useState({});
  const [validationStates, setValidationStates] = useState({});
  const [participants, setParticipants] = useState([
    { id: 1, name: '', location: '', transport: '' },
    { id: 2, name: '', location: '', transport: '' }
  ]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [currentMeetingId, setCurrentMeetingId] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(['cafe', 'restaurant']);
  const [radius, setRadius] = useState(5);

  // Show error message if redirected from results page
  useEffect(() => {
    if (location?.state?.error) {
      toast?.error(location?.state?.error, {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      // Clear the error from state
      navigate(location?.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const mockLocations = [
    { name: 'Central Park', address: '59th to 110th Street, Manhattan, NY 10022' },
    { name: 'Times Square', address: 'Manhattan, NY 10036' },
    { name: 'Brooklyn Bridge', address: 'Brooklyn Bridge, New York, NY 10038' },
    { name: 'Empire State Building', address: '350 5th Ave, New York, NY 10118' },
    { name: 'Statue of Liberty', address: 'Liberty Island, New York, NY 10004' }
  ];

  const handleValidationChange = (index, isValid) => {
    setValidationStates(prev => ({
      ...prev,
      [index]: isValid
    }));
  };

  const handleNameChange = (index, value) => {
    const updated = [...participants];
    updated[index].name = value;
    setParticipants(updated);
    
    if (errors?.[index]?.name) {
      const newErrors = { ...errors };
      delete newErrors?.[index]?.name;
      setErrors(newErrors);
    }
  };

  const handleLocationChange = (index, value) => {
    const updated = [...participants];
    updated[index].location = value;
    setParticipants(updated);
    
    if (errors?.[index]?.location) {
      const newErrors = { ...errors };
      delete newErrors?.[index]?.location;
      setErrors(newErrors);
    }
  };

  const handleTransportChange = (index, value) => {
    const updated = [...participants];
    updated[index].transport = value;
    setParticipants(updated);
    
    if (errors?.[index]?.transport) {
      const newErrors = { ...errors };
      delete newErrors?.[index]?.transport;
      setErrors(newErrors);
    }
  };

  const handleRemoveParticipant = (index) => {
    if (participants?.length > 2) {
      const updated = participants?.filter((_, i) => i !== index);
      setParticipants(updated);
      
      const newErrors = { ...errors };
      delete newErrors?.[index];
      setErrors(newErrors);
    }
  };

  const handleAddParticipant = () => {
    if (participants?.length < 10) {
      setParticipants([
        ...participants,
        { id: Date.now(), name: '', location: '', transport: '' }
      ]);
    }
  };

  const handleUseCurrentLocation = (index) => {
    if (navigator.geolocation) {
      navigator.geolocation?.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position?.coords;
          
          // Try to reverse geocode the coordinates to get actual address
          try {
            const apiKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;
            
            if (apiKey && apiKey !== 'your-google-maps-api-key-here') {
              // Use Google Geocoding API to convert coordinates to address
              const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
              );
              const data = await response?.json();
              
              if (data?.status === 'OK' && data?.results?.[0]) {
                const address = data?.results?.[0]?.formatted_address;
                handleLocationChange(index, address);
                return;
              }
            }
            
            // Fallback: Use coordinates as location string
            handleLocationChange(index, `${latitude?.toFixed(6)}, ${longitude?.toFixed(6)}`);
          } catch (error) {
            console.error('Reverse geocoding error:', error);
            // Fallback: Use coordinates as location string
            handleLocationChange(index, `${latitude?.toFixed(6)}, ${longitude?.toFixed(6)}`);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to detect your location. Please ensure location permissions are enabled.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    participants?.forEach((participant, index) => {
      if (!participant?.name?.trim()) {
        if (!newErrors?.[index]) newErrors[index] = {};
        newErrors[index].name = 'Name is required';
        isValid = false;
      }
      if (!participant?.location?.trim()) {
        if (!newErrors?.[index]) newErrors[index] = {};
        newErrors[index].location = 'Location is required';
        isValid = false;
      }
      if (!participant?.transport) {
        if (!newErrors?.[index]) newErrors[index] = {};
        newErrors[index].transport = 'Transportation method is required';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Validate address using Google Geocoding API
  const validateAddress = async (address) => {
    const apiKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey || apiKey === 'your-google-maps-api-key-here') {
      return { valid: false, error: 'Google Maps API key not configured. Please add your API key to continue.' };
    }

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
      );
      const data = await response?.json();

      if (data?.status === 'OK' && data?.results?.[0]) {
        const result = data?.results?.[0];
        const location = result?.geometry?.location;
        
        // Check if the result is a partial match (incomplete address)
        if (result?.partial_match) {
          return { 
            valid: false, 
            error: 'Address is incomplete. Please provide a more specific address with street, city, and state/country.' 
          };
        }
        
        // Check if we got a valid coordinate
        if (location?.lat && location?.lng) {
          return { valid: true, coordinates: { lat: location?.lat, lng: location?.lng } };
        }
      }
      
      // Handle specific error statuses
      if (data?.status === 'ZERO_RESULTS') {
        return { valid: false, error: 'Address not found. Please check the spelling and try again.' };
      } else if (data?.status === 'INVALID_REQUEST') {
        return { valid: false, error: 'Invalid address format. Please enter a valid address.' };
      } else {
        return { valid: false, error: 'Unable to validate address. Please check and try again.' };
      }
    } catch (error) {
      console.error('Address validation error:', error);
      return { valid: false, error: 'Network error. Please check your connection and try again.' };
    }
  };

  const handleCalculate = async () => {
    // First check for empty fields
    const newErrors = {};
    let hasEmptyFields = false;

    participants?.forEach((participant, index) => {
      if (!participant?.name?.trim()) {
        newErrors[index] = { ...newErrors?.[index], name: 'Name is required' };
        hasEmptyFields = true;
      }
      if (!participant?.location?.trim()) {
        newErrors[index] = { ...newErrors?.[index], location: 'Location is required' };
        hasEmptyFields = true;
      }
      if (!participant?.transport) {
        newErrors[index] = { ...newErrors?.[index], transport: 'Transportation method is required' };
        hasEmptyFields = true;
      }
    });

    if (hasEmptyFields) {
      setErrors(newErrors);
      // Scroll to first error
      const firstErrorIndex = Object.keys(newErrors)?.[0];
      if (firstErrorIndex !== undefined) {
        const errorElement = document.querySelector(`[data-participant-index="${firstErrorIndex}"]`);
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Check validation states from real-time validation
    const invalidAddresses = [];
    participants?.forEach((participant, index) => {
      if (validationStates?.[index] === false) {
        invalidAddresses?.push(index);
      }
    });

    if (invalidAddresses?.length > 0) {
      const validationErrors = {};
      invalidAddresses?.forEach(index => {
        validationErrors[index] = { 
          location: 'Please select a valid address from the dropdown suggestions' 
        };
      });
      setErrors(validationErrors);
      return;
    }

    // Validate all addresses using geocoding API (final check)
    setIsValidating(true);
    const validationErrors = {};
    let hasValidationErrors = false;

    for (let index = 0; index < participants?.length; index++) {
      const participant = participants?.[index];
      const validation = await validateAddress(participant?.location);
      
      if (!validation?.valid) {
        validationErrors[index] = { 
          ...validationErrors?.[index], 
          location: validation?.error 
        };
        hasValidationErrors = true;
      }
    }

    setIsValidating(false);

    if (hasValidationErrors) {
      setErrors(validationErrors);
      return;
    }

    // All addresses are valid, create meeting record and proceed
    setIsCalculating(true);
    
    try {
      // Create meeting record in database
      const currentDate = new Date();
      const meetingData = {
        locationName: 'Calculating midpoint...',
        locationAddress: 'Location will be determined after calculation',
        meetingDate: currentDate?.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
        meetingTime: currentDate?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        status: 'pending',
        locationType: 'midpoint'
      };
      
      const createdMeeting = await createMeeting(meetingData);
      
      if (!createdMeeting || !createdMeeting?.id) {
        throw new Error('Failed to create meeting record');
      }
      
      setCurrentMeetingId(createdMeeting?.id);
      
      // Navigate to results with meeting ID and participant data
      navigate(`/meeting-point-results?meetingId=${createdMeeting?.id}`, { 
        state: { 
          meetingId: createdMeeting?.id,
          sessionCode: createdMeeting?.sessionCode,
          selectedTypes: selectedTypes,
          radius: radius,
          participants: participants?.map(p => ({
            id: p?.id,
            name: p?.name,
            location: p?.location,
            transport: p?.transport
          }))
        } 
      });
    } catch (error) {
      console.error('Error creating meeting:', error);
      setErrors({ general: 'Failed to create meeting. Please try again.' });
    } finally {
      setIsCalculating(false);
    }
  };

  const handleInviteParticipant = () => {
    setShowInviteModal(true);
  };

  // Check if all inputs are valid for Calculate button
  const isFormValid = () => {
    // Must have at least 2 participants
    if (participants?.length < 2) return false;
    
    // Check each participant has all required fields
    for (let i = 0; i < participants?.length; i++) {
      const participant = participants?.[i];
      
      // Check name
      if (!participant?.name?.trim()) return false;
      
      // Check location
      if (!participant?.location?.trim()) return false;
      
      // Check transport
      if (!participant?.transport) return false;
      
      // Check address validation state (must be explicitly valid)
      if (validationStates?.[i] !== true) return false;
    }
    
    return true;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <StepProgressIndicator />
      <WorkflowBreadcrumb />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 pb-32">
        <div className="mb-6 md:mb-8">
          <div className="flex items-start gap-4">
            <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-xl flex-shrink-0">
              <Icon name="Users" size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Setup Your Meeting
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Enter participant locations and transportation preferences to find the perfect meeting point with equal travel time for everyone
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6 md:space-y-8">
          <QuickStartGuide />

          <ParticipantList
            participants={participants}
            onNameChange={handleNameChange}
            onLocationChange={handleLocationChange}
            onTransportChange={handleTransportChange}
            onRemove={handleRemoveParticipant}
            onAdd={handleAddParticipant}
            onUseCurrentLocation={handleUseCurrentLocation}
            onValidationChange={handleValidationChange}
            errors={errors}
          />

          {participants?.length >= 2 && (
            <div className="flex gap-3 mb-6">
              <button
                onClick={handleInviteParticipant}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Icon name="UserPlus" size={20} />
                <span className="font-medium">Invite Participant</span>
              </button>
            </div>
          )}

          {participants?.length >= 2 && (
            <div className="bg-card border border-border rounded-xl p-4 md:p-6">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-heading font-semibold text-foreground mb-2">
                    Fair Meeting Coordination
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Our algorithm calculates meeting points based on actual travel time, not distance. This ensures everyone arrives at the same time, regardless of their transportation method or starting location.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={16} className="text-success" />
                      <span className="caption text-muted-foreground">Equal travel time</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={16} className="text-success" />
                      <span className="caption text-muted-foreground">Multiple location options</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Navigation" size={16} className="text-success" />
                      <span className="caption text-muted-foreground">Real-time directions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Filter" size={16} className="text-success" />
                      <span className="caption text-muted-foreground">Venue type filtering</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <NavigationActions
        onNext={handleCalculate}
        isNextDisabled={!isFormValid()}
        isLoading={isCalculating || isValidating}
        nextLabel={
          isValidating 
            ? 'Validating Addresses...' 
            : isCalculating 
            ? 'Calculating...' :'Calculate Meeting Points'
        }
      />
      <InviteParticipantModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        meetingId={currentMeetingId}
        inviterName={user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Someone'}
      />
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default LocationInput;